import { KeyboardCode, type Announcements, type ScreenReaderInstructions, type UniqueIdentifier } from '@dnd-kit/core';
import type { KeyboardCoordinateGetter } from '@dnd-kit/core';
import type { CrmDealListItem, CrmStage } from '@ip/shared-types';
import { t } from '@/i18n';

/**
 * Keyboard plumbing for the deal board, shared by the board and the sheet so
 * both handlers agree on what "the user is typing" means and on what the
 * `]` `[` `W` `L` letters do.
 */

/**
 * True when the key press belongs to a text control — shortcuts must stay out.
 * `closest` also covers cmdk / Radix combobox inputs nested in role=listbox wrappers.
 */
export function isTypingTarget(event: { target: EventTarget | null }): boolean {
  return (
    event.target instanceof HTMLElement &&
    !!event.target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')
  );
}

/** Any open Radix modal layer (dialog / alert dialog / sheet) owns the keyboard. */
export function hasOpenRadixLayer(): boolean {
  return !!document.querySelector(
    '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]',
  );
}

/** The card element of a deal on the board (null when it is not rendered). */
export function findCard(dealId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-deal-id="${CSS.escape(dealId)}"]`);
}

export interface StageShortcutTarget {
  stageId: string;
  /** Set when the target is a terminal stage (the move opens the confirmation). */
  close?: 'won' | 'lost';
}

/**
 * `]` / `[` step through the open stages, `W` / `L` aim at the terminal ones.
 * Null when the key is not a stage shortcut, the deal is closed, or there is
 * no stage in that direction — the caller then leaves the event alone.
 */
export function stageShortcutTarget(
  key: string,
  deal: Pick<CrmDealListItem, 'stageId' | 'closedAt'>,
  stages: { open: CrmStage[]; won?: CrmStage; lost?: CrmStage },
): StageShortcutTarget | null {
  if (deal.closedAt !== null) return null;
  const index = stages.open.findIndex((stage) => stage.id === deal.stageId);
  switch (key.toLowerCase()) {
    case ']': {
      const next = index >= 0 ? stages.open[index + 1] : undefined;
      return next ? { stageId: next.id } : null;
    }
    case '[': {
      const previous = index > 0 ? stages.open[index - 1] : undefined;
      return previous ? { stageId: previous.id } : null;
    }
    case 'w':
      return stages.won ? { stageId: stages.won.id, close: 'won' } : null;
    case 'l':
      return stages.lost ? { stageId: stages.lost.id, close: 'lost' } : null;
    default:
      return null;
  }
}

/**
 * Where a keyboard-moved card lands inside its target column: below the
 * header, so rectIntersection unambiguously picks that column even for the
 * narrow (w-40) rails.
 */
const KEYBOARD_DROP_OFFSET_Y = 48;

/**
 * Left/right arrows jump the lifted card to the previous/next droppable in
 * `ids` (the visual column order, rails last). The returned coordinates are
 * the card's new top-left: centred horizontally inside the target column and
 * `KEYBOARD_DROP_OFFSET_Y` below its top. Up/down are ignored — there is
 * nothing to reorder inside a column. Scrolling the board is left to the
 * sensor's built-in clamping, which scrolls the ancestor and adjusts the delta.
 */
export function makeCoordinateGetter(ids: ReadonlyArray<UniqueIdentifier>): KeyboardCoordinateGetter {
  return (event, { context, currentCoordinates }) => {
    if (event.code !== KeyboardCode.Left && event.code !== KeyboardCode.Right) return undefined;
    const { droppableRects, collisionRect, over, active } = context;
    const activeStage = active?.data.current?.stageId as UniqueIdentifier | undefined;
    const currentId = over?.id ?? activeStage;
    let index = currentId !== undefined ? ids.indexOf(currentId) : -1;
    if (index < 0) {
      // Fall back to whichever droppable the card currently overlaps the most.
      let best = -1;
      ids.forEach((id, i) => {
        const rect = droppableRects.get(id);
        if (!rect || !collisionRect) return;
        const overlap =
          Math.min(rect.right, collisionRect.right) - Math.max(rect.left, collisionRect.left);
        if (overlap > best) {
          best = overlap;
          index = i;
        }
      });
    }
    const step = event.code === KeyboardCode.Right ? 1 : -1;
    // Skip droppables that are not registered (e.g. an inactive-stage column is not a target).
    for (let next = index + step; next >= 0 && next < ids.length; next += step) {
      const rect = droppableRects.get(ids[next]);
      if (!rect) continue;
      event.preventDefault();
      const width = collisionRect?.width ?? 0;
      return {
        x: rect.left + Math.max(0, (rect.width - width) / 2),
        y: rect.top + KEYBOARD_DROP_OFFSET_Y,
      };
    }
    return currentCoordinates;
  };
}

export interface AnnouncementLookups {
  dealName(id: UniqueIdentifier): string;
  stageName(id: UniqueIdentifier | undefined): string;
}

/** Live-region sentences (pl/en via `t`) for the dnd-kit accessibility layer. */
export function makeAnnouncements(lookups: AnnouncementLookups): Announcements {
  return {
    onDragStart: ({ active }) =>
      t('crm.deals.dnd.start', {
        name: lookups.dealName(active.id),
        stage: lookups.stageName(active.data.current?.stageId as UniqueIdentifier | undefined),
      }),
    onDragOver: ({ active, over }) =>
      over
        ? t('crm.deals.dnd.over', { name: lookups.dealName(active.id), stage: lookups.stageName(over.id) })
        : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? t('crm.deals.dnd.end', { name: lookups.dealName(active.id), stage: lookups.stageName(over.id) })
        : t('crm.deals.dnd.cancel'),
    onDragCancel: () => t('crm.deals.dnd.cancel'),
  };
}

// A language switch reloads the page (UserMenu), so resolving `t` once at
// module load is safe.
export const SCREEN_READER_INSTRUCTIONS: ScreenReaderInstructions = {
  draggable: t('crm.deals.dnd.instructions'),
};
